/**
 * Export Utilities for Transactions and Disputes
 * Provides functionality to export data to Excel format
 */

import { type Transaction } from "@/types/transaction";
import { type DisputeUI } from "@/types/ui/disputes";
import { formatDateDDMMYYYY, formatCurrency } from "@/lib/utils";

/**
 * Export transactions to Excel (CSV format)
 * @param transactions - Array of transactions to export
 * @param filename - Optional filename (defaults to transactions-{date}.csv)
 */
export function exportTransactionsToExcel(
  transactions: Transaction[],
  filename?: string
): void {
  if (transactions.length === 0) {
    throw new Error("No transactions to export");
  }

  // Define columns to export
  const columns = [
    { key: "referenceNumber", label: "Reference Number" },
    { key: "transactionDate", label: "Transaction Date" },
    { key: "dueDate", label: "Due Date" },
    { key: "customerName", label: "Customer Name" },
    { key: "transactionType", label: "Type" },
    { key: "currency", label: "Currency" },
    { key: "amount", label: "Gross Amount" },
    { key: "outstandingAmount", label: "Outstanding Amount" },
    { key: "status", label: "Status" },
    { key: "description", label: "Description" },
    { key: "accountCode", label: "Account Code" },
    { key: "netAmount", label: "Net Amount" },
    { key: "vatAmount", label: "VAT Amount" },
    { key: "onDispute", label: "On Dispute" },
    { key: "subLedger", label: "Sub Ledger" },
    { key: "orderNumber", label: "Order Number" },
    { key: "currencyGross", label: "Currency Gross" },
    { key: "currencyNet", label: "Currency Net" },
    { key: "currencyVAT", label: "Currency VAT" },
    { key: "currencyOutstanding", label: "Currency Outstanding" },
    { key: "createdBy", label: "Created By" },
    { key: "createdAt", label: "Created At" },
    { key: "modifiedBy", label: "Modified By" },
    { key: "modifiedAt", label: "Modified At" },
  ];

  // Create CSV header
  const header = columns.map((col) => `"${col.label}"`).join(",");

  // Create CSV rows
  const rows = transactions.map((transaction) => {
    return columns
      .map((col) => {
        const key = col.key as keyof Transaction;
        let value = transaction[key];

        // Format specific fields
        if (key === "transactionDate" || key === "dueDate") {
          value = value ? formatDateDDMMYYYY(value as string) : "";
        } else if (key === "createdAt" || key === "modifiedAt") {
          value = value ? new Date(value as string).toLocaleString() : "";
        } else if (key === "amount" || key === "outstandingAmount" || key === "netAmount" || key === "vatAmount" || key === "currencyGross" || key === "currencyNet" || key === "currencyVAT" || key === "currencyOutstanding") {
          value = value !== undefined ? formatCurrency(value as number, transaction.currency) : "";
        } else if (key === "transactionType") {
          value = formatTransactionType(value as string);
        } else if (key === "status") {
          value = formatStatus(value as string);
        } else if (key === "onDispute") {
          value = value ? "Yes" : "No";
        } else if (value === undefined || value === null) {
          value = "";
        }

        // Escape quotes and wrap in quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(",");
  });

  // Combine header and rows
  const csv = [header, ...rows].join("\n");

  // Create blob and download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    filename || `transactions-${new Date().toISOString().split("T")[0]}.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Format transaction type for display
 */
function formatTransactionType(type: string): string {
  const typeMap: Record<string, string> = {
    invoice: "Invoice",
    credit_note: "Credit Note",
    receipt: "Receipt",
    debit_adjustment: "Debit Adjustment",
    credit_adjustment: "Credit Adjustment",
  };
  return typeMap[type] || type;
}

/**
 * Format status for display
 */
function formatStatus(status: string): string {
  const statusMap: Record<string, string> = {
    pending: "Pending",
    completed: "Completed",
    cancelled: "Cancelled",
    void: "Void",
    draft: "Draft",
    overdue: "Overdue",
    partially_paid: "Partially Paid",
  };
  return statusMap[status] || status;
}

/**
 * Calculate totals for selected transactions
 */
export interface TransactionTotals {
  count: number;
  totalAmount: number;
  totalOutstanding: number;
  currency: string;
}

export function calculateTransactionTotals(
  transactions: Transaction[]
): TransactionTotals {
  if (transactions.length === 0) {
    return {
      count: 0,
      totalAmount: 0,
      totalOutstanding: 0,
      currency: "GBP",
    };
  }

  const totalAmount = transactions.reduce((sum, t) => sum + t.amount, 0);
  const totalOutstanding = transactions.reduce(
    (sum, t) => sum + (t.outstandingAmount || 0),
    0
  );

  // Use the currency from the first transaction
  const currency = transactions[0].currency;

  return {
    count: transactions.length,
    totalAmount,
    totalOutstanding,
    currency,
  };
}

/**
 * Export allocation history to Excel (CSV format)
 * @param allocationHistory - Array of allocation history entries to export
 * @param filename - Optional filename (defaults to allocation-history-{date}.csv)
 */
export function exportAllocationHistoryToExcel(
  allocationHistory: Array<{
    id: number;
    accountNumber: string;
    customerName: string;
    transactionDate: string;
    type: string;
    referenceNumber: string;
    gross: number;
    allocatedValue: number;
    allocatedDate: string;
    currency: string;
    currencyGross: number;
    currencyAllocValue: number;
    currencyCurrency: string;
    allocationRef: string;
    userName: string;
  }>,
  filename?: string,
  visibleColumns?: string[]
): void {
  if (allocationHistory.length === 0) {
    throw new Error("No allocation history to export");
  }

  // Define all available columns
  const allColumns = [
    { key: "accountNumber", label: "Account #" },
    { key: "customerName", label: "Customer Name" },
    { key: "transactionDate", label: "Transaction Date" },
    { key: "type", label: "Type" },
    { key: "referenceNumber", label: "Reference Number" },
    { key: "gross", label: "Gross" },
    { key: "allocatedValue", label: "Allocated Value" },
    { key: "allocatedDate", label: "Allocated Date" },
    { key: "currency", label: "Currency" },
    { key: "currencyGross", label: "Currency Gross" },
    { key: "currencyAllocValue", label: "Currency Alloc. Value" },
    { key: "allocationRef", label: "Allocation Ref" },
    { key: "userName", label: "User Name" },
  ];

  // Filter columns based on visibility
  const columns = visibleColumns
    ? allColumns.filter((col) => visibleColumns.includes(col.key))
    : allColumns;

  // Create CSV header
  const header = columns.map((col) => `"${col.label}"`).join(",");

  // Create CSV rows
  const rows = allocationHistory.map((entry) => {
    return columns
      .map((col) => {
        const key = col.key as keyof typeof entry;
        let value = entry[key];

        // Format specific fields
        if (key === "transactionDate" || key === "allocatedDate") {
          value = value ? formatDateDDMMYYYY(value as string) : "";
        } else if (key === "gross" || key === "allocatedValue") {
          value = value !== undefined ? formatCurrency(value as number, entry.currency) : "";
        } else if (key === "currencyGross" || key === "currencyAllocValue") {
          value = value !== undefined ? formatCurrency(value as number, entry.currencyCurrency) : "";
        } else if (key === "type") {
          value = formatTransactionType(value as string);
        } else if (value === undefined || value === null) {
          value = "";
        }

        // Escape quotes and wrap in quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(",");
  });

  // Combine header and rows
  const csv = [header, ...rows].join("\n");

  // Create blob and download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    filename || `allocation-history-${new Date().toISOString().split("T")[0]}.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Export disputes to Excel (CSV format)
 * @param disputes - Array of disputes to export
 * @param filename - Optional filename (defaults to disputes-{date}.csv)
 */
export function exportDisputesToExcel(
  disputes: DisputeUI[],
  filename?: string
): void {
  if (disputes.length === 0) {
    throw new Error("No disputes to export");
  }

  // Define columns to export
  const columns = [
    { key: "id", label: "Dispute ID" },
    { key: "customer", label: "Customer" },
    { key: "customerAccountCode", label: "Account Code" },
    { key: "contactName", label: "Contact Name" },
    { key: "contactEmail", label: "Contact Email" },
    { key: "contactPhone", label: "Contact Phone" },
    { key: "type", label: "Type" },
    { key: "amount", label: "Amount" },
    { key: "status", label: "Status" },
    { key: "priority", label: "Priority" },
    { key: "assignedTo", label: "Assigned To" },
    { key: "createdDate", label: "Created Date" },
    { key: "dueDate", label: "Due Date" },
    { key: "workflow", label: "Workflow" },
    { key: "currentLevel", label: "Current Level" },
    { key: "totalLevels", label: "Total Levels" },
    { key: "aiScore", label: "AI Score" },
    { key: "aiRecommendation", label: "AI Recommendation" },
    { key: "description", label: "Description" },
    { key: "resolutionNotes", label: "Resolution Notes" },
  ];

  // Create CSV header
  const header = columns.map((col) => `"${col.label}"`).join(",");

  // Create CSV rows
  const rows = disputes.map((dispute) => {
    return columns
      .map((col) => {
        const key = col.key as keyof DisputeUI;
        let value = dispute[key];

        // Format specific fields
        if (key === "createdDate" || key === "dueDate") {
          value = value ? formatDateDDMMYYYY(value as string) : "";
        } else if (key === "amount") {
          value = value !== undefined ? formatCurrency(value as number, "GBP") : "";
        } else if (key === "aiScore") {
          value = value !== null && value !== undefined ? String(value) : "";
        } else if (value === undefined || value === null) {
          value = "";
        }

        // Escape quotes and wrap in quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(",");
  });

  // Combine header and rows
  const csv = [header, ...rows].join("\n");

  // Create blob and download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    filename || `disputes-${new Date().toISOString().split("T")[0]}.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
